import {
  DATABASE_LARGE_DATA_POLICY,
  DATABASE_PRODUCT_LIMITS,
  DOCUMENT_PRODUCT_HARD_LIMIT_BYTES,
} from '@streamlain/database-identity';

import type { ImportLimitDetails } from './import-limit-error';

type ImportLimitCatalogEntry = {
  /** Enforced value; null where it depends on the column type or is none. */
  readonly limit: number | null;
  readonly unit: 'rows' | 'columns' | 'bytes' | 'chars';
  /** File-import modules that refuse with this kind. */
  readonly producedIn: readonly string[];
  readonly reason: string;
};

const { excelFileHardLimitBytes, excelExpandedHardLimitBytes } =
  DATABASE_LARGE_DATA_POLICY.import;

/**
 * Every limit the file import enforces, with its value from the
 * database-identity policy and the reason for it. Each kind is shown with the
 * DE/EN message `com.streamlain.import.preview.limit.<kind>`, which names the
 * limit and what the user can do instead.
 */
export const IMPORT_LIMIT_CATALOG = {
  rows: {
    limit: DATABASE_PRODUCT_LIMITS.rowHardLimit,
    unit: 'rows',
    producedIn: ['import-preflight.ts', 'import-limit-error.ts'],
    reason:
      'A database holds no more rows for users and integrations; the system headroom is kept for concurrent offline work.',
  },
  columns: {
    limit: DATABASE_PRODUCT_LIMITS.columnHardLimit,
    unit: 'columns',
    producedIn: ['import-preflight.ts', 'import-limit-error.ts'],
    reason:
      'A database holds no more columns for users and integrations; the system headroom is kept for concurrent offline work.',
  },
  cell: {
    // Per column type: DATABASE_LARGE_DATA_POLICY.cell.limits.
    limit: null,
    unit: 'chars',
    producedIn: ['import-limit-error.ts'],
    reason:
      'An imported value meets the same cell limit of its column type as a typed one.',
  },
  'row-bytes': {
    limit: DATABASE_PRODUCT_LIMITS.rowEncodedByteHardLimit,
    unit: 'bytes',
    producedIn: ['import-limit-error.ts'],
    reason: 'A row has to fit into its row shard document.',
  },
  'record-bytes': {
    limit: DATABASE_PRODUCT_LIMITS.rowEncodedByteHardLimit,
    unit: 'bytes',
    producedIn: ['csv-row-stream.ts'],
    reason:
      'The CSV stream holds one pending record at a time; a record larger than a row could not be stored and would grow the buffer without bound.',
  },
  'root-index-bytes': {
    limit: DOCUMENT_PRODUCT_HARD_LIMIT_BYTES,
    unit: 'bytes',
    producedIn: ['import-preflight.ts'],
    reason:
      'The row addresses of a streamed import have to fit into the database root document.',
  },
  'excel-file-size': {
    limit: excelFileHardLimitBytes,
    unit: 'bytes',
    producedIn: ['excel-workbook-bytes.ts'],
    reason:
      'Excel workbooks load whole into memory; a larger file is refused before it is read. CSV streams.',
  },
  'excel-expanded-size': {
    limit: excelExpandedHardLimitBytes,
    unit: 'bytes',
    producedIn: ['excel-workbook-bytes.ts'],
    reason:
      'The unpacked size tracks the memory the workbook reader needs; it is checked before parsing. CSV streams.',
  },
  empty: {
    limit: null,
    unit: 'rows',
    producedIn: ['import-preflight.ts'],
    reason: 'A source without data rows creates no database.',
  },
} as const satisfies Record<
  ImportLimitDetails['kind'],
  ImportLimitCatalogEntry
>;
